import React, { useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Form, Field } from 'react-final-form'
import ReCAPTCHA from 'react-google-recaptcha'
import createDecorator from 'final-form-calculate'
import createFocusDecorator from 'final-form-focus'
import { bool, string } from 'prop-types'

import { RECAPTCHA_SITE_KEY } from 'common/constants'
import {
	ActionButton,
	ErrorBullets,
	ErrorList,
	FieldComponent,
	FieldLabel,
	FieldsContainer,
	FormField,
	FormWrapper,
} from '@jelly/components'
import {
	composeValidators,
	getErrorMessages,
	handleOptOutOrDataRequestSubmission,
	isValidEmail,
	required,
	STATES,
} from '@jelly/utils'

// final-form form decorators
const focusOnError = createFocusDecorator()

const prefillStateDecorator = createDecorator(
	// Update state dropdown selection based on Cali resident radio button selection
	{
		field: 'isCAResident',
		updates: {
			state: (isCAResident, { state }) => {
				const stateToApply = state !== 'CA' ? state : -1

				return isCAResident === 'true' ? 'CA' : stateToApply
			},
		},
	},
	// Update Cali resident radio button based on state dropdown selection
	{
		field: 'state',
		updates: {
			isCAResident: (state) => (state === 'CA' ? 'true' : 'false'),
		},
	}
)

const RadioGroup = styled.div`
	border: 1px solid ${({ theme }) => theme.colors.gray[400]};
	box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
	border-radius: 4px;
	display: flex;
	align-items: center;
	width: calc(100% - 1em);
	padding: 8px;
	margin: 8px 0 0 0;
	font-size: 100%;
	color: ${({ theme }) => theme.colors.gray[500]};

	${({ isSelected, theme }) =>
		isSelected &&
		`
		border: 1px solid ${theme.colors.secondary};
	`}

	input[type="radio"] {
		position: absolute;
		opacity: 0;
		+ label {
			&:before {
				content: '';
				background: ${({ theme }) => theme.colors.white};
				border-radius: 100%;
				border: 1px solid ${({ theme }) => theme.colors.gray[400]};
				display: inline-block;
				width: 1rem;
				height: 1rem;
				position: relative;
				margin-right: 0.5rem;
			}
		}
		&:checked {
			+ label {
				&:before {
					background-color: ${({ theme }) => theme.colors.secondary};
					border: 1px solid ${({ theme }) => theme.colors.secondary};
					box-shadow: inset 0 0 0 4px ${({ theme }) => theme.colors.white};
				}
			}
		}
		&:focus {
			+ label {
				&:before {
					border-color: ${({ theme }) => theme.colors.secondary};
					box-shadow: 0 0 0 2px white, 0 0 0 4px ${({ theme }) => theme.colors.secondary},
						inset 0 0 0 4px ${({ theme }) => theme.colors.white};
				}
			}
		}
	}
`

const ButtonDiv = styled.div`
	padding: 1rem 0;
	margin: 1rem 0;
`

const CaliResidentsOnly = styled.span`
	display: flex;
	justify-content: flex-start;
	padding: 0.5rem 0 0 0;
	color: ${({ theme }) => theme.colors.red};
	font-size: 14px;
`

const FieldWrapper = styled.div`
	width: 80%;
	margin: auto;
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	${({ theme }) => theme.MQ.M} {
		padding-top: 20px;
		width: 40%;
		padding: 4px;
		margin: 8px;
	}

	select:disabled {
		background-color: ${({ theme }) => theme.colors.gainsboro}; /* what is this? */
		cursor: default;

		&::placeholder {
			color: ${({ theme }) => theme.colors.gray[300]};
		}
	}
`
const CustomFieldLabel = styled.label`
	display: block;
	font-size: 90%;
	font-weight: 700;
	color: ${({ theme }) => theme.colors.gray[500]};
	padding: 0.5em 0;
	width: 100%;
	cursor: pointer;
	display: flex;
	align-items: center;
`

const FormHeader = styled.header`
	width: 80%;
	margin: 0 auto;
	color: ${({ theme }) => theme.colors.gray[700]};
	${({ theme }) => theme.text.bodySmall};
`

const ThankYouHeader = styled.header`
	margin: 2rem auto 5rem;
	color: ${({ theme }) => theme.colors.gray[500]};
	width: 80%;
	${({ theme }) => theme.text.bodySmall};

	${({ theme }) => theme.MQ.M} {
		min-height: 25rem;
	}
`

const ErrorMessage = styled.div`
	color: ${({ theme }) => theme.colors.red};
	padding-top: 5px;
	font-size: 12px;
	line-height: 15px;
	display: inline-flex;
`

const initialValues = {
	isCAResident: '',
	notes: '',
	firstName: '',
	lastName: '',
	email: '',
	phone: '',
	companyName: '',
	street: '',
	city: '',
	state: '',
	zipcode: '',
	recaptchaToken: '',
}

function getInitialValues(isDataRequestForm) {
	return !isDataRequestForm
		? initialValues
		: {
				...initialValues,
				isCAResident: 'true',
				state: 'CA',
		  }
}

CcpaForm.propTypes = {
	isDataRequestForm: bool,
	formIntroText: string.isRequired,
}

function CcpaForm({ isDataRequestForm = false, formIntroText }) {
	const recaptchaRef = useRef()
	const [isNotResidentOfCa, setIsNotResidentOfCa] = useState(isDataRequestForm ? false : true)
	const [isFormSubmitted, setIsFormSubitted] = useState(false)

	if (isFormSubmitted) {
		return (
			<ThankYouHeader>
				<p>
					Thank you. A confirmation email has been sent and we will process your request within{' '}
					{isDataRequestForm ? `45` : `15`} days. If you have any questions, please contact us at
					888-393-5000.
				</p>
			</ThankYouHeader>
		)
	}

	return (
		<>
			<FormHeader>
				<p>{formIntroText}</p>
			</FormHeader>
			<FormWrapper>
				<Form
					initialValues={getInitialValues(isDataRequestForm)}
					decorators={[focusOnError, prefillStateDecorator]}
					mutators={{
						setRecaptchaField: (args, state, utils) => {
							utils.changeValue(state, 'recaptchaToken', () => args[0])
						},
					}}
					onSubmit={async (values) => {
						await handleOptOutOrDataRequestSubmission(values, isDataRequestForm)

						setIsFormSubitted(true)
						window.scrollTo({
							top: 0,
							left: 0,
							behavior: 'smooth',
						})
					}}
					render={({ submitErrors, handleSubmit, submitting, pristine, form: { mutators }, invalid }) => {
						return (
							<form
								id="ccpaForm"
								onSubmit={() => {
									if (invalid) {
										recaptchaRef.current.reset()
									}

									handleSubmit()
								}}
							>
								{submitErrors && (
									<ErrorList>
										{getErrorMessages(submitErrors, Object.keys(initialValues)).map((message) => (
											<ErrorBullets key={message} dangerouslySetInnerHTML={{ __html: message }} />
										))}
									</ErrorList>
								)}
								<FieldsContainer>
									<FieldWrapper>
										<FieldLabel>Are you a resident of the state of California?</FieldLabel>
										<div>
											<Field name="isCAResident" type="radio" value="true">
												{({ input }) => (
													<RadioGroup isSelected={input.checked}>
														<input
															id="residentOfCaYes"
															type="radio"
															{...input}
															onChange={(e) => {
																input.onChange(e) //final-form's onChange
																setIsNotResidentOfCa(false)
															}}
														/>
														<CustomFieldLabel htmlFor="residentOfCaYes">
															Yes
														</CustomFieldLabel>
													</RadioGroup>
												)}
											</Field>
											<Field name="isCAResident" type="radio" value="false">
												{({ input }) => (
													<RadioGroup isSelected={input.checked}>
														<input
															id="residentOfCaNo"
															type="radio"
															{...input}
															onChange={(e) => {
																input.onChange(e) //final-form's onChange
																setIsNotResidentOfCa(true)
															}}
														/>
														<CustomFieldLabel htmlFor="residentOfCaNo">No</CustomFieldLabel>
													</RadioGroup>
												)}
											</Field>
										</div>
										{isDataRequestForm && isNotResidentOfCa && (
											<CaliResidentsOnly>
												This process is for California residents only
											</CaliResidentsOnly>
										)}
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>
											Please provide any additional information if desired (optional):
										</FieldLabel>
										<FormField name="notes" type="textarea" component="textarea" rows="5" />
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>First Name:</FieldLabel>
										<FormField name="firstName" type="text" validate={required} required />
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>Last Name:</FieldLabel>
										<FormField name="lastName" type="text" validate={required} required />
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>Email Address:</FieldLabel>
										<Field name="email" validate={composeValidators(required, isValidEmail)}>
											{({ input, meta }) => (
												<>
													{meta.error && meta.dirty ? (
														<ErrorMessage>{meta.error}</ErrorMessage>
													) : null}
													<FieldComponent {...input} type="email" meta={meta} />
												</>
											)}
										</Field>
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>Phone Number:</FieldLabel>
										<FormField name="phone" type="tel" validate={required} required />
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>Company Name [If applicable]:</FieldLabel>
										<FormField name="companyName" type="text" />
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>Street Address:</FieldLabel>
										<FormField name="street" type="text" validate={required} required />
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>City:</FieldLabel>
										<FormField name="city" type="text" validate={required} required />
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>State:</FieldLabel>
										<FormField
											name="state"
											component="select"
											type="text"
											validate={required}
											required
											disabled={isDataRequestForm}
										>
											<option value="">Please Choose One</option>
											{Object.keys(STATES).map((stateAbrv) => (
												<option value={stateAbrv} key={stateAbrv}>
													{STATES[stateAbrv]}
												</option>
											))}
										</FormField>
									</FieldWrapper>
									<FieldWrapper>
										<FieldLabel>Zip Code:</FieldLabel>
										<FormField name="zipcode" type="text" validate={required} required />
									</FieldWrapper>
									<FieldWrapper />
									<ReCAPTCHA
										ref={recaptchaRef}
										onChange={(token) => {
											// Form mutator adds hidden state to the form to handle storing our recaptcha token once we have it.
											mutators.setRecaptchaField(token)

											// We trigger the form submit here because we have to do it when we know we have the token.
											// We need to do this check to support IE11
											let event
											if (typeof Event === 'function') {
												event = new Event('submit', {
													cancelable: true,
												})
											} else {
												event = document.createEvent('Event')
												event.initEvent('submit', true, true)
											}
											document.getElementById('ccpaForm').dispatchEvent(event)
										}}
										size="invisible"
										sitekey={RECAPTCHA_SITE_KEY}
									/>
									<FieldWrapper>
										<ButtonDiv>
											<ActionButton
												type="button"
												disabled={
													submitting || pristine || (isDataRequestForm && isNotResidentOfCa)
												}
												active={!pristine}
												onClick={() => recaptchaRef.current.execute()}
											>
												Submit
											</ActionButton>
										</ButtonDiv>
									</FieldWrapper>
									<FieldWrapper />
								</FieldsContainer>
							</form>
						)
					}}
				/>
			</FormWrapper>
		</>
	)
}

export default CcpaForm
